import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Edit3,
  Eye,
  Filter,
  Plus,
  Search,
  Trash2,
  WalletCards,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { adminApi, publicAssetUrl } from "../../lib/adminApi";

const statusOptions = ["all", "planned", "ongoing", "completed", "suspended"];

function statusClass(status) {
  const s = String(status || "ongoing").toLowerCase();
  if (s === "completed") return "bg-emerald-100 text-emerald-800";
  if (s === "ongoing") return "bg-blue-100 text-blue-800";
  if (s === "suspended") return "bg-red-100 text-red-800";
  return "bg-amber-100 text-amber-800";
}

function money(value) {
  if (!value) return "Not stated";
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(number);
}

function percent(value) {
  return Math.max(0, Math.min(100, Number(value || 0)));
}

export default function AdminProjectList() {
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function loadProjects() {
    setLoading(true);
    setError("");
    try {
      const data = await adminApi("/api/admin/projects");
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProjects();
  }, []);

  async function deleteProject(project) {
    if (!window.confirm(`Delete "${project.title}"?`)) return;
    try {
      await adminApi(`/api/admin/projects/${project.id}`, { method: "DELETE" });
      setProjects((items) => items.filter((item) => item.id !== project.id));
    } catch (err) {
      alert(err.message || "Delete failed");
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((item) => {
      const matchesStatus = status === "all" || String(item.status || "").toLowerCase() === status;
      const haystack = `${item.title} ${item.project_type} ${item.funding_source} ${item.location} ${item.contractor}`.toLowerCase();
      return matchesStatus && haystack.includes(q);
    });
  }, [projects, query, status]);

  const total = projects.length;
  const ongoing = projects.filter((p) => String(p.status).toLowerCase() === "ongoing").length;
  const completed = projects.filter((p) => String(p.status).toLowerCase() === "completed").length;
  const avg = Math.round(projects.reduce((sum, p) => sum + percent(p.completion_percentage), 0) / (projects.length || 1));

  return (
    <AdminLayout
      title="TETFund / Project Tracker CMS"
      subtitle="Create, update and publish official TETFund and institutional development projects."
      actions={
        <Link to="/adxm/projects/create" className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-3 text-sm font-black text-white shadow hover:bg-emerald-800">
          <Plus size={18} /> Add TETFund Project
        </Link>
      }
    >
      <div className="space-y-6">
        {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 font-bold text-red-700">{error}</div>}

        <section className="grid gap-4 md:grid-cols-4">
          {[
            ["Total Projects", total, Building2, "bg-slate-900 text-white"],
            ["Ongoing", ongoing, Clock3, "bg-blue-700 text-white"],
            ["Completed", completed, CheckCircle2, "bg-emerald-700 text-white"],
            ["Average Progress", `${avg}%`, WalletCards, "bg-violet-700 text-white"],
          ].map(([label, value, Icon, cls]) => (
            <div key={label} className={`rounded-3xl p-5 shadow-sm ${cls}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-black uppercase tracking-[0.18em] opacity-75">{label}</p>
                <Icon size={24} className="opacity-80" />
              </div>
              <p className="mt-3 text-3xl font-black">{value}</p>
            </div>
          ))}
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 focus-within:border-emerald-400 focus-within:ring-4 focus-within:ring-emerald-100">
              <Search size={18} className="text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search title, funding source, contractor, location..."
                className="w-full bg-transparent text-sm font-semibold outline-none"
              />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <Filter size={18} className="text-slate-500" />
              {statusOptions.map((item) => (
                <button
                  key={item}
                  onClick={() => setStatus(item)}
                  className={`rounded-full px-4 py-2 text-sm font-black capitalize ${
                    status === item ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-2">
          {loading && Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-64 animate-pulse rounded-3xl bg-slate-200" />)}

          {!loading && filtered.map((project) => {
            const image = publicAssetUrl(project.image_url);
            const progress = percent(project.completion_percentage);
            return (
              <article key={project.id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                <div className="grid md:grid-cols-[220px_1fr]">
                  <div className="relative min-h-[220px] bg-gradient-to-br from-slate-900 to-emerald-900">
                    {image ? (
                      <img src={image} alt={project.title} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : null}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                    <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-black text-emerald-800">
                      {project.project_type || "TETFund"}
                    </span>
                  </div>

                  <div className="p-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-black capitalize ${statusClass(project.status)}`}>{project.status}</span>
                      {project.is_featured ? <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-black text-yellow-800">Featured</span> : null}
                    </div>

                    <h2 className="mt-3 text-xl font-black leading-tight text-slate-950">{project.title}</h2>
                    <p className="mt-2 text-sm font-semibold text-slate-500">
                      {project.location || "Location not stated"} • {money(project.budget_amount)}
                    </p>

                    <div className="mt-4">
                      <div className="mb-1 flex justify-between text-xs font-black text-slate-500">
                        <span>Completion</span><span>{progress}%</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-emerald-500" style={{ width: `${progress}%` }} />
                      </div>
                    </div>

                    <p className="mt-4 line-clamp-2 text-sm leading-6 text-slate-600">{project.description || "No project description yet."}</p>

                    <div className="mt-5 flex flex-wrap gap-2">
                      <Link to={`/tetfund-projects/${project.slug}`} target="_blank" className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-200">
                        <Eye size={15} /> View Public
                      </Link>
                      <Link to={`/adxm/projects/edit/${project.id}`} className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-3 py-2 text-xs font-black text-white hover:bg-blue-800">
                        <Edit3 size={15} /> Edit
                      </Link>
                      <button onClick={() => deleteProject(project)} className="inline-flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-xs font-black text-red-700 hover:bg-red-100">
                        <Trash2 size={15} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </section>

        {!loading && filtered.length === 0 && (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <Building2 className="mx-auto h-12 w-12 text-emerald-700" />
            <h2 className="mt-4 text-2xl font-black text-slate-950">No project found</h2>
            <p className="mt-2 text-slate-600">Create your first TETFund project or change your filter.</p>
            <Link to="/adxm/projects/create" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-3 font-black text-white">
              Add TETFund Project <ArrowRight size={18} />
            </Link>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
